import { useCallback, useEffect, useRef, useState } from "react";

const FLUSH_DEBOUNCE_MS = 120;

function resolveExternalValue(
  storeValue: unknown,
  propValue: unknown,
  defaultValue: unknown,
): string {
  if (storeValue !== undefined) return String(storeValue ?? "");
  if (propValue !== undefined) return String(propValue ?? "");
  return String(defaultValue ?? "");
}

/**
 * 大表单场景：输入先写本地 draft（即时响应），再 debounce 批量 flush 到 store（减少全局订阅风暴）。
 */
export function useBufferedStoreFieldValue(
  storeValue: unknown,
  propValue: unknown,
  defaultValue: unknown,
  flush: (value: string) => void,
  enabled: boolean,
): [string, (value: string) => void, () => void] {
  const resolve = () => resolveExternalValue(storeValue, propValue, defaultValue);

  const [draft, setDraft] = useState(resolve);
  const lastFlushedRef = useRef(resolve());
  const pendingRef = useRef<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledFlush = useCallback(() => {
    if (debounceRef.current != null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setDraft(resolve());
      return;
    }
    const external = resolve();
    if (external !== lastFlushedRef.current && pendingRef.current === null) {
      setDraft(external);
      lastFlushedRef.current = external;
    }
  }, [enabled, storeValue, propValue, defaultValue]);

  const flushNow = useCallback(
    (value: string) => {
      pendingRef.current = null;
      if (value === lastFlushedRef.current) return;
      lastFlushedRef.current = value;
      flush(value);
    },
    [flush],
  );

  const scheduleFlush = useCallback(
    (value: string) => {
      pendingRef.current = value;
      cancelScheduledFlush();
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        const pending = pendingRef.current;
        if (pending != null) flushNow(pending);
      }, FLUSH_DEBOUNCE_MS);
    },
    [cancelScheduledFlush, flushNow],
  );

  const onDraftChange = useCallback(
    (value: string) => {
      setDraft(value);
      if (enabled) scheduleFlush(value);
    },
    [enabled, scheduleFlush],
  );

  const onBlurFlush = useCallback(() => {
    if (!enabled) return;
    cancelScheduledFlush();
    flushNow(draft);
  }, [enabled, cancelScheduledFlush, draft, flushNow]);

  useEffect(
    () => () => {
      cancelScheduledFlush();
    },
    [cancelScheduledFlush],
  );

  if (!enabled) {
    return [resolve(), (value: string) => flush(value), () => flush(resolve())];
  }

  return [draft, onDraftChange, onBlurFlush];
}
